import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router";
import { AppShell } from "@/components/layout/AppShell";
import { LoadingBlock } from "@/components/ui/States";
import { LoginPage } from "@/features/auth/LoginPage";
import { SignupPage } from "@/features/auth/SignupPage";
import { VerifyEmailPage } from "@/features/auth/VerifyEmailPage";
import { AffiliationsPage } from "@/features/affiliations/AffiliationsPage";
import { AssignmentsPage } from "@/features/assignments/AssignmentsPage";
import { AvailabilityPage } from "@/features/availability/AvailabilityPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { ContractPage } from "@/features/contract/ContractPage";
import { PayrollPage } from "@/features/payroll/PayrollPage";
import { PlanPage } from "@/features/plan/PlanPage";
import { PricingPage } from "@/features/pricing/PricingPage";
import { InvitationsPage } from "@/features/invitations/InvitationsPage";
import { ProfilePage } from "@/features/profile/ProfilePage";
import { SportsPage } from "@/features/sports/SportsPage";
import { useAuth } from "@/providers/AuthProvider";
import { useI18n } from "@/hooks/useI18n";
import type { ReactNode } from "react";

function RequireAuth({ children }: { children: ReactNode }) {
    const { coach, isAuthenticated, initializing } = useAuth();
    const { t } = useI18n();

    if (initializing) {
        return (
            <div className="app-page-scaffold flex min-h-screen items-center justify-center">
                <LoadingBlock label={t("auth.restoring")} />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Verifikasi wajib — sesi yang belum verified dipaksa ke halaman OTP.
    if (coach && !coach.emailVerifiedAt) {
        return (
            <Navigate
                to="/verify-email"
                replace
                state={{ email: coach.email ?? undefined }}
            />
        );
    }

    return <>{children}</>;
}

export default function App() {
    return (
        <Router>
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route
                    element={
                        <RequireAuth>
                            <AppShell />
                        </RequireAuth>
                    }
                >
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/assignments" element={<AssignmentsPage />} />
                    <Route path="/affiliations" element={<AffiliationsPage />} />
                    <Route path="/sports" element={<SportsPage />} />
                    <Route path="/plan" element={<PlanPage />} />
                    <Route path="/availability" element={<AvailabilityPage />} />
                    <Route path="/contract" element={<ContractPage />} />
                    <Route path="/invitations" element={<InvitationsPage />} />
                            <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/payroll" element={<PayrollPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                </Route>
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </Router>
    );
}
